import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGetEigeneAuftraegeMutation } from '../../api/AuftragAPI';
import { useGetAuftragTableOrderServiceMutation } from '../../api/ProfileAPI';
import { LoadingModal } from '../../component/LoadingModal/LoadingModal';
import ScrollToTopBadge from '../../component/ScrollToTop';
import { formatToEuroDate, getWochentagUtil } from '../../util/DateUtils';
import ButtonAuftragHideComponent from './component/ButtonAuftragHideComponent';
import PaginatorComponent from './component/PaginatorComponent';

/**
 * @author noone
 */
export type IEigeneAuftraegeListeProps = {
  statusChanged: () => void;
}

export type PaginatorDto = {
  offset: number,
  limit: number,
  totalCount: number,
  data?: any[],
}

export default function EigeneAuftraegeListe(props: IEigeneAuftraegeListeProps) {

  const { t } = useTranslation();

  const [getProfileParameter] = useGetAuftragTableOrderServiceMutation();
  const [eigeneauftraegeAPI] = useGetEigeneAuftraegeMutation();

  const [liste, setListe] = useState<PaginatorDto>({ offset: 0, limit: 10, totalCount: 0, data: [] });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [search, setSearch] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("");
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [onlyVisible, setOnlyVisible] = useState<boolean>(true);
  const [sortDirection, setSortDirection] = useState<string>("");

  const navigate = useNavigate();

  const sort = (column: string) => {

    if (sortBy !== column) {
      setSortDirection("asc");
    } else if (sortDirection === "") {
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else if (sortDirection === "desc") {
      setSortDirection("");
    }

    setSortBy(column);
  }

  const lade = () => {
    setIsLoading(true);
    eigeneauftraegeAPI({
      offset: offset,
      limit: limit,
      onlyVisible: onlyVisible,
      search: search,
      sortBy: sortBy,
      sortDirection: sortDirection
    }
    ).unwrap()
      .then((data) => setListe(data))
      .catch((error) => console.log(error))
      .finally(() => setIsLoading(false))
      ;
  }

  const getIconBySort = (column: string) => {
    if (column !== sortBy) {
      return "bi bi-arrow-down-up";
    } else if (sortDirection === "asc") {
      return "bi bi-arrow-up";
    } else if (sortDirection === "desc") {
      return "bi bi-arrow-down";
    } return "bi bi-arrow-down-up";
  }

  const suche = (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();

    setOffset(0);
    lade();
  }

  const getFarbeZuStatus = (status: string) => {
    if (status === "NEU") {
      return "lightblue";
    } else if (status === "IN_TERMINIERUNG") {
      return "lightblue";
    } else if (status === "IN_TERMINIERUNG") {
      return "yellow";
    } else if (status === "FERTIG") {
      return "lightgreen";
    } else
      return "white";
  }

  const pageChangeHandler = (page: number) => {
    setOffset(page * limit - limit);
  }

  // Load profile settings on component mount
  useEffect(() => {
    getProfileParameter("eigeneauftraegetablesort").unwrap().then((dto) => {
      var intDirection: number = 0;
      let columnToSort = "";

      if (dto.art !== 0) {
        columnToSort = "art";
        intDirection = dto.art;
      }
      if (dto.status !== 0) {
        columnToSort = "status";
        intDirection = dto.status;
      }
      if (dto.auftragsnr !== 0) {
        columnToSort = "auftragnr";
        intDirection = dto.auftragnr;
      }
      if (dto.name !== 0) {
        columnToSort = "name";
        intDirection = dto.name;
      }
      if (dto.lieferort !== 0) {
        columnToSort = "lieferort";
        intDirection = dto.lieferort;
      }
      if (dto.termin !== 0) {
        columnToSort = "termin";
        intDirection = dto.termin;
      }
      if (dto.erstellt_am !== 0) {
        columnToSort = "erstellt_am";
        intDirection = dto.erstellt_am;
      }

      setSortBy(columnToSort);
      if (intDirection === 1) {
        setSortDirection("asc");
      } else if (intDirection === -1) {
        setSortDirection("desc");
      }
    }).catch((error) => {
      console.log(error);
      setSortBy("");
      setSortDirection("");
    });
  }, []);

  // Load data when sort or offset changes
  useEffect(() => {
    // window.scrollTo(0, 0);
    lade();
  }, [onlyVisible, sortBy, sortDirection, offset]);

  return (

    <div>

      <LoadingModal show={isLoading} />

      <h3>{t('deTranslation.eigeneAuftraege.titel')}</h3>

      <form onSubmit={suche} style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "40px" }}>
        <div>
          Suche :
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <input type="text" className='form-control' placeholder='Suche' value={search} onChange={(e) => setSearch(e.target.value)} size={50} />
          <button type='submit' className="btn btn-primary">Suchen</button>
        </div>
      </form>

      <div>
        Nur sichtbare Aufträge anzeigen:
        <input type="checkbox" checked={onlyVisible} onChange={() => setOnlyVisible(!onlyVisible)} style={{ marginLeft: "10px" }} />
      </div>

      <PaginatorComponent pageChange={(e) => pageChangeHandler(e)} offset={offset} limit={limit} totalCount={liste.totalCount} />

      {liste?.data?.length === 0 && <div>Keine Aufträge vorhanden</div>}

      <table className="table table-striped">
        <thead>
          <tr>
            <th onClick={() => { sort("art") }}><span translate="no">Art</span> <i style={{ marginLeft: "10px" }} className={getIconBySort("art")} /></th>
            <th onClick={() => { sort("status") }}><span translate="no">Status</span> <i style={{ marginLeft: "10px" }} className={getIconBySort("status")} /></th>
            <th onClick={() => { sort("name") }}><span translate="no">Name</span> <i style={{ marginLeft: "10px" }} className={getIconBySort("name")} /></th>
            <th onClick={() => { sort("auftragnr") }}><span translate="no">Auftragsnr</span> <i style={{ marginLeft: "10px" }} className={getIconBySort("auftragnr")} /></th>
            <th onClick={() => { sort("lieferort") }}><span translate="no">Lieferort</span> <i style={{ marginLeft: "10px" }} className={getIconBySort("lieferort")} /></th>
            <th onClick={() => { sort("termin") }}><span translate="no">Termin</span> <i style={{ marginLeft: "10px" }} className={getIconBySort("termin")} /></th>
            <th onClick={() => { sort("erstellt_am") }}><span translate="no">Erstellt am</span> <i style={{ marginLeft: "10px" }} className={getIconBySort("erstellt_am")} /></th>
            <th>Option</th>
          </tr>
        </thead>
        <tbody>
          {
            liste?.data?.map((e, index) => (
              <tr key={e.uid} >
                <td style={{ padding: "12px 16px" }}>{e.art}</td>
                <td style={{ padding: "12px 16px" }}><label style={{ borderRadius: "10px", border: "none", padding: "10px", backgroundColor: getFarbeZuStatus(e.status) }}>{e.status}</label></td>
                <td style={{ padding: "12px 16px" }}>{e.vorname_endkunde} {e.name_endkunde}</td>
                <td style={{ padding: "12px 16px" }}>{e.auftragnr}</td>
                <td style={{ padding: "12px 16px" }}>{e.stadt_lieferadresse}</td>
                <td style={{ padding: "12px 16px" }}>{getWochentagUtil(e.aktuellerTermin)} {formatToEuroDate(e.aktuellerTermin)}</td>
                <td style={{ padding: "12px 16px" }}>{getWochentagUtil(e.createDate)} {formatToEuroDate(e.createDate)}</td>
                <td style={{ padding: "12px 16px" }}>
                  <button title={t('deTranslation.eigeneAuftraege.seeDetails')} className="btn btn-outline-secondary" onClick={() => navigate("eigeneauftraege/detail/" + e.uid)}><i title={t('deTranslation.eigeneAuftraege.seeDetails')} style={{ cursor: "pointer", fontSize: "18pt" }} className="bi bi-pencil"></i></button>
                  <button title={t('deTranslation.eigeneAuftraege.duplicate')} className="btn btn-outline-secondary" onClick={() => navigate("neuerauftrag", { state: { action: 'copy', uid: e.uid } })}><i translate="no" title={t('deTranslation.eigeneAuftraege.duplicate')} style={{ cursor: "pointer", fontSize: "18pt" }} className="bi bi-copy"></i></button>
                  <ButtonAuftragHideComponent uidAuftrag={e.uid} onClick={() => lade()} visibility={e.visibleForVerkaeufer} />
                </td>
              </tr>
            ))
          }
        </tbody >
      </table >

      <PaginatorComponent pageChange={(e) => pageChangeHandler(e)} offset={offset} limit={limit} totalCount={liste.totalCount} />

      <ScrollToTopBadge />

    </div >
  );

}

